import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { Copy } from '@styled-icons/boxicons-regular';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { Form, Formik } from 'formik';
import stepComponents from './steps';
import { STATUS } from 'constants/thunkStates';
import {
  newOperationInitialState,
  newOperationValidationSchema,
} from 'forms/newOperation';
import { createOne, updateOne } from 'redux/slices/dataSlice';
import { dataSelector } from 'redux/selectors';
import LimitedBackdropLoading from 'components/Loading/LimitedBackdropLoading';
import StatusChip from 'components/StatusChip';
import { BASE_CLIENT_URL } from 'constants/index';

const StyledButton = styled(Button)`
  margin-left: 1rem;
  margin-right: 1rem;
`;

const CopyIcon = styled(Copy)`
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
`;

function NewOperationForm({ handleClose, id, clientData, readOnly, ...rest }) {
  const dispatch = useDispatch();
  const { status, error } = useSelector(dataSelector);
  const [step, setStep] = useState(1);
  const [localStatus, setLocalStatus] = useState(STATUS.idle);
  const isLastStep = step === _.keys(stepComponents).length;
  const filledClientData = _.merge(
    {
      ...newOperationInitialState.stepOne,
      ...newOperationInitialState.stepTwo,
    },
    { ...clientData }
  );

  const stepForward = () => {
    if (step === 2) return;
    setStep(step + 1);
  };
  const stepBackward = () => {
    if (step === 1) {
      return handleClose();
    }
    setStep(step - 1);
  };

  useEffect(() => {
    setLocalStatus(status);
  }, [status]);

  const handleSubmit = (values, formik) => {
    if (isLastStep) {
      if (readOnly) return;
      _.isNumber(id)
        ? dispatch(updateOne(_.merge({}, clientData, values)))
        : dispatch(createOne(values));

      formik.setTouched({});
      formik.setSubmitting(true);
      localStatus === STATUS.success && handleClose();
    } else {
      stepForward();
      formik.setTouched({});
      formik.setSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    return navigator.clipboard.writeText(
      `${BASE_CLIENT_URL}/${clientData.hash}`
    );
  };

  const formProps = { handleSubmit, stepForward, stepBackward };

  let FormComponent;
  FormComponent = stepComponents[step];

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="stretch"
      xs={12}
    >
      <Grid
        item
        container
        // spacing={3}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Operación' : 'Nueva Operación'}
        </Typography>
        <Box ml={1} display="flex" flexDirection="row">
          {id && (
            <>
              <StatusChip status={clientData?.id_ob_operation_status} />
              <CopyIcon onClick={handleCopyLink} />
            </>
          )}
        </Box>
      </Grid>
      <Formik
        onSubmit={handleSubmit}
        initialValues={
          clientData
            ? { ...filledClientData }
            : {
                ...newOperationInitialState.stepOne,
                ...newOperationInitialState.stepTwo,
              }
        }
        validationSchema={newOperationValidationSchema}
      >
        {(formik) => (
          <Form>
            <Grid
              item
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              xs={12}
            >
              <FormComponent {...formProps} readOnly={readOnly} id={id} />
            </Grid>
            <Box mt={2}>
              <Grid
                item
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <StyledButton
                  variant="contained"
                  color="secondary"
                  onClick={stepBackward}
                  disabled={status === STATUS.pending ? true : false}
                >
                  {step === 1 ? (readOnly ? 'Salir' : 'Cancelar') : 'Atrás'}
                </StyledButton>
                {(step === 2 && readOnly) || (
                  <StyledButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={status === STATUS.pending ? true : false}
                  >
                    {isLastStep ? 'Finalizar' : 'Siguiente'}
                  </StyledButton>
                )}
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>
      {localStatus === STATUS.pending && <LimitedBackdropLoading size={50} />}
      {localStatus === STATUS.error && (error.description || error.message)}
    </Grid>
  );
}

export default NewOperationForm;
